<template>
    <div class="icon-select-panel-wrapper">
        <!-- 아이콘 미리보기 버튼 -->
        <div class="icon-ctn" @click="toggleDropdown" ref="button">
            <i v-if="getCurrentIconClass()" :class="getCurrentIconClass()" class="preview-icon">
                <template v-if="getPreviewIconText()">{{ getPreviewIconText() }}</template>
            </i>
            <i v-else class="fa fa-times"></i>
        </div>

        <!-- 드롭다운 팝업 -->
        <div v-if="isDropdownOpen" class="icon-dropdown font-roboto-slab" ref="dropdown">
            <!-- 최상위 탭: Material Icons vs FontAwesome Icons -->
            <div v-if="iconType === 'both' && getAvailableMainTabs().length > 1" class="main-icon-tabs">
                <button
                    v-for="(mainTab, index) in getAvailableMainTabs()"
                    :key="index"
                    class="main-tab"
                    :class="{ active: selectedMainTab === index }"
                    @click="selectMainTab(index)"
                >
                    <span class="tab-label">{{ mainTab.label }}</span>
                </button>
            </div>

            <!-- 서브 탭: Material Icons 스타일 또는 FontAwesome 카테고리 -->
            <div v-if="getCurrentSubTabs() && getCurrentSubTabs().length > 0" class="icon-type-tabs">
                <button
                    v-for="(subTab, index) in getCurrentSubTabs()"
                    :key="index"
                    class="icon-tab"
                    :class="{ active: selectedSubTab === index }"
                    @click="selectSubTab(index)"
                >
                    <span class="tab-label">{{ subTab.label }}</span>
                </button>
            </div>

            <!-- 아이콘 그리드 -->
            <div class="icon-palette-container">
                <div class="icon-grid">
                    <div
                        v-for="(icon, index) in getFilteredIcons()"
                        :key="getIconKey(icon, index)"
                        class="icon-item"
                        :class="{ selected: isIconSelected(icon) }"
                        @click="selectIcon(icon)"
                        :title="icon.title"
                    >
                        <i :class="getIconClass(icon)" class="icon-display">
                            <template v-if="isMaterialIcon()">{{ icon.title }}</template>
                        </i>
                        <i v-if="isIconSelected(icon)" class="fa fa-check check-icon"></i>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// 오프라인 Material Icons CSS import
import "material-icons/iconfont/material-icons.css";
import Icons from "@/plugins/FontAwesomeIcons.js";

export default {
    name: "IconSelectPanel",
    props: {
        value: {
            type: Object,
            default: null,
        },
        // 기존 IconSelect 호환성을 위한 prop
        current: {
            type: String,
            default: null,
        },
        // 'material', 'fontawesome', 'both'
        iconType: {
            type: String,
            default: "both",
            validator: (value) => ["material", "fontawesome", "both"].includes(value),
        },
    },
    data() {
        return {
            selectedIcon: null,
            selectedMainTab: 0,
            selectedSubTab: 0,
            isDropdownOpen: false,
            mainIconTabs: [
                {
                    label: "머티리얼 아이콘",
                    type: "material",
                    subTabs: [
                        { label: "기본", class: "material-icons", value: "filled" },
                        { label: "외곽선", class: "material-icons-outlined", value: "outlined" },
                        { label: "라운드", class: "material-icons-round", value: "round" },
                        { label: "샤프", class: "material-icons-sharp", value: "sharp" },
                        { label: "투톤", class: "material-icons-two-tone", value: "two-tone" },
                    ],
                    icons: Icons.Material || [],
                },
                {
                    label: "폰트어썸 아이콘",
                    type: "fontawesome",
                    subTabs: [
                        { label: "폰트어썸", class: "fas", value: "fontawesome", icons: Icons.FontAwesome || [] },
                        { label: "심플라인", class: "icon", value: "simpleline", icons: Icons.Icons || [] },
                        { label: "안드로이드", class: "ion-md", value: "android", icons: Icons.IonMd || [] },
                        { label: "iOS", class: "ion-ios", value: "ios", icons: Icons.IonIos || [] },
                        { label: "로고", class: "ion-logo", value: "logo", icons: Icons.IonLogo || [] },
                    ],
                },
            ],
        };
    },
    created() {
        this.initializeSelectedIcon();
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        // 초기화 메서드
        initializeSelectedIcon() {
            if (this.value) {
                this.updateSelectedIcon(this.value);
            } else if (this.current) {
                const convertedIcon = this.convertCurrentToIcon(this.current);
                this.updateSelectedIcon(convertedIcon);
            }
        },
        // selectedIcon 업데이트 및 이벤트 emit
        updateSelectedIcon(iconData) {
            this.selectedIcon = iconData;
            this.emitIconChange(iconData);
        },
        // 아이콘 변경 이벤트 emit
        emitIconChange(iconData) {
            this.$emit("input", iconData);
            this.$emit("change", iconData);
            this.$emit("on-change", iconData);
        },
        // availableMainTabs 계산
        getAvailableMainTabs() {
            if (this.iconType === "material") {
                return [this.mainIconTabs[0]];
            } else if (this.iconType === "fontawesome") {
                return [this.mainIconTabs[1]];
            }
            return this.mainIconTabs;
        },
        // currentMainTab 계산
        getCurrentMainTab() {
            const availableTabs = this.getAvailableMainTabs();
            if (!availableTabs || availableTabs.length === 0) {
                return null;
            }
            return availableTabs[this.selectedMainTab] || availableTabs[0] || null;
        },
        // currentSubTabs 계산
        getCurrentSubTabs() {
            const currentMainTab = this.getCurrentMainTab();
            if (!currentMainTab) {
                return [];
            }
            return currentMainTab.subTabs || [];
        },
        // filteredIcons 계산
        getFilteredIcons() {
            const currentMainTab = this.getCurrentMainTab();
            let icons = [];

            if (currentMainTab.type === "material") {
                icons = currentMainTab.icons || [];
            } else {
                const currentSubTab = currentMainTab.subTabs[this.selectedSubTab];
                icons = currentSubTab.icons || [];
            }

            return icons;
        },
        // currentIconClass 계산
        getCurrentIconClass() {
            if (!this.selectedIcon) {
                if (this.current) {
                    // Material Icons인 경우 클래스만 추출
                    if (this.current.includes("material-icons")) {
                        const materialClassMatch = this.current.match(/material-icons[^-]*/);
                        return materialClassMatch ? materialClassMatch[0].trim() : "material-icons";
                    }
                    return this.current;
                }
                return "";
            }

            if (this.selectedIcon.materialClass) {
                return this.selectedIcon.materialClass;
            } else if (this.selectedIcon.icon) {
                if (this.selectedIcon.icon.startsWith("material ")) {
                    return "material-icons";
                }
                return this.selectedIcon.icon;
            } else {
                return "material-icons";
            }
        },
        // previewIconText 계산
        getPreviewIconText() {
            if (this.selectedIcon) {
                if (this.selectedIcon.materialClass) {
                    return this.getSelectedIconName();
                } else if (this.selectedIcon.icon && this.selectedIcon.icon.startsWith("material ")) {
                    return this.getSelectedIconName();
                }
                return "";
            } else if (this.current && this.current.includes("material-icons")) {
                const iconName = this.getMaterialIconName(this.current);
                return iconName || "";
            }
            return "";
        },
        // selectedIconName 계산
        getSelectedIconName() {
            if (!this.selectedIcon) return "";
            return this.selectedIcon.title || "";
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },
        handleClickOutside(event) {
            if (
                this.$refs.button &&
                !this.$refs.button.contains(event.target) &&
                this.$refs.dropdown &&
                !this.$refs.dropdown.contains(event.target)
            ) {
                this.isDropdownOpen = false;
            }
        },
        selectMainTab(index) {
            this.selectedMainTab = index;
            this.selectedSubTab = 0;
        },
        selectSubTab(index) {
            this.selectedSubTab = index;
        },
        selectIcon(icon) {
            // Material Icons인 경우 현재 선택된 서브 탭의 클래스 정보도 함께 저장
            const iconData = { ...icon };
            const currentMainTab = this.getCurrentMainTab();
            if (currentMainTab && currentMainTab.type === "material") {
                const materialClass =
                    currentMainTab.subTabs && currentMainTab.subTabs[this.selectedSubTab]
                        ? currentMainTab.subTabs[this.selectedSubTab].class
                        : "material-icons";
                iconData.materialClass = materialClass;
                // Material Icons의 경우 icon 속성을 "material-icons ac_unit" 형식으로 생성
                iconData.icon = `${materialClass} ${icon.title || ""}`.trim();
            } else {
                // FontAwesome 계열인 경우 icon 속성이 이미 있거나 생성 필요
                if (!iconData.icon && iconData.title) {
                    // icon 속성이 없으면 title을 사용 (필요시 조정)
                    iconData.icon = iconData.title;
                }
            }
            this.updateSelectedIcon(iconData);
            this.isDropdownOpen = false;
        },
        isIconSelected(icon) {
            if (!this.selectedIcon) return false;

            const currentMainTab = this.getCurrentMainTab();
            if (!currentMainTab) return false;

            // Material Icons인 경우 materialClass도 함께 비교
            if (currentMainTab.type === "material") {
                const currentMaterialClass =
                    currentMainTab.subTabs && currentMainTab.subTabs[this.selectedSubTab]
                        ? currentMainTab.subTabs[this.selectedSubTab].class
                        : "material-icons";
                const selectedMaterialClass = this.selectedIcon.materialClass;

                if (currentMaterialClass !== selectedMaterialClass) {
                    return false;
                }

                return this.selectedIcon.title === icon.title;
            } else {
                // FontAwesome 계열은 title과 icon으로 비교
                const currentIcons = this.getFilteredIcons();
                const isInCurrentTab = currentIcons.some(
                    (currentIcon) =>
                        currentIcon.title === this.selectedIcon.title && currentIcon.icon === this.selectedIcon.icon
                );

                if (!isInCurrentTab) return false;

                return this.selectedIcon.title === icon.title && this.selectedIcon.icon === icon.icon;
            }
        },
        getIconClass(icon) {
            const currentMainTab = this.getCurrentMainTab();
            if (!currentMainTab) {
                return icon.icon || "material-icons";
            }

            if (currentMainTab.type === "material") {
                return currentMainTab.subTabs && currentMainTab.subTabs[this.selectedSubTab]
                    ? currentMainTab.subTabs[this.selectedSubTab].class
                    : "material-icons";
            } else {
                return (
                    icon.icon ||
                    (currentMainTab.subTabs && currentMainTab.subTabs[this.selectedSubTab]
                        ? currentMainTab.subTabs[this.selectedSubTab].class
                        : "")
                );
            }
        },
        isMaterialIcon() {
            const currentMainTab = this.getCurrentMainTab();
            return currentMainTab && currentMainTab.type === "material";
        },
        // 아이콘의 고유한 key 생성 (중복 방지)
        getIconKey(icon, index) {
            const currentMainTab = this.getCurrentMainTab();
            if (!currentMainTab) {
                return `icon-${index}`;
            }

            let prefix = "";

            if (currentMainTab.type === "material") {
                prefix = `material-${this.selectedMainTab}-${this.selectedSubTab}`;
            } else {
                prefix = `fa-${this.selectedMainTab}-${this.selectedSubTab}`;
            }

            const iconIdentifier = icon.icon || icon.title || "";
            return `${prefix}-${iconIdentifier}-${index}`;
        },
        // 기존 IconSelect 호환성: current 문자열을 icon 객체로 변환
        convertCurrentToIcon(currentString) {
            if (!currentString) return null;

            let allIcons = [];
            this.mainIconTabs.forEach((tab) => {
                if (tab.type === "material") {
                    allIcons = allIcons.concat(tab.icons || []);
                } else {
                    tab.subTabs.forEach((subTab) => {
                        allIcons = allIcons.concat(subTab.icons || []);
                    });
                }
            });

            const foundIcon = allIcons.find((icon) => {
                if (icon.icon === currentString) return true;
                if (currentString.includes("material-icons")) {
                    const materialClassMatch = currentString.match(/material-icons[^-]*/);
                    if (materialClassMatch) {
                        const iconName = currentString.replace(/material-icons[^-]*/g, "").trim();
                        if (icon.title === iconName) {
                            return true;
                        }
                    }
                }
                return false;
            });

            if (foundIcon) {
                if (currentString.includes("material-icons")) {
                    const materialClassMatch = currentString.match(/material-icons[^-]*/);
                    const materialClass = materialClassMatch ? materialClassMatch[0] : "material-icons";
                    return { ...foundIcon, materialClass };
                }
                return foundIcon;
            }

            return null;
        },
        // Material Icons 문자열에서 아이콘 이름 추출
        getMaterialIconName(currentString) {
            if (!currentString || !currentString.includes("material-icons")) return "";
            // "material-icons ac_unit" -> "ac_unit"
            // "material-icons-outlined ac_unit" -> "ac_unit"
            const parts = currentString.trim().split(/\s+/);
            // 첫 번째 부분이 material-icons 관련 클래스이므로 제거
            if (parts.length > 1 && parts[0].includes("material-icons")) {
                return parts.slice(1).join(" ");
            }
            return "";
        },
    },
};
</script>

<style scoped>
.icon-select-panel-wrapper {
    position: relative;
    display: inline-block;
    width: 5rem;
    height: 5rem;
}

/* 아이콘 미리보기 버튼 - 기존 디자인 유지 */
.icon-ctn {
    position: relative;
    width: 5rem;
    height: 5rem;
    background-color: #eceff1;
    cursor: pointer;
    font-size: 2rem;
    color: #455a64;
    border-radius: 5px;
    border: 1px solid #cfd8dc;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: background-color 0.3s;
}

.icon-ctn:hover {
    color: #eceff1;
    background-color: #455a64;
}

.preview-icon {
    font-size: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* 드롭다운 팝업 */
.icon-dropdown {
    position: absolute;
    top: calc(100% + 8px);
    /* left: 0 !important; */
    right: 2px !important;
    width: 400px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    overflow: hidden;
    transform: translateX(0) !important;
}

/* 최상위 탭 (Material Icons vs FontAwesome Icons) */
.main-icon-tabs {
    display: flex;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
}

.main-tab {
    flex: 1;
    padding: 12px 16px;
    border: none;
    border-bottom: 2px solid transparent;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: #6c757d;
    font-size: 12px;
    font-weight: 500;
}

.main-tab:hover {
    background: #e9ecef;
    color: #495057;
}

.main-tab.active {
    background: white;
    color: #007bff;
    border-bottom-color: #007bff;
    font-weight: 600;
}

.main-tab:focus {
    outline: none;
}

/* 아이콘 타입 탭 (서브 탭) */
.icon-type-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
}

.icon-tab {
    padding: 6px 14px;
    border: none;
    border-radius: 20px;
    background: white;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: #6c757d;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid #dee2e6;
    white-space: nowrap;
}

.icon-tab:hover {
    background: #e9ecef;
    color: #495057;
    border-color: #adb5bd;
}

.icon-tab.active {
    background: #007bff;
    color: white;
    border-color: #007bff;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25);
}

.icon-tab:focus {
    outline: none;
}

.tab-label {
    font-weight: 500;
}

/* 아이콘 팔레트 */
.icon-palette-container {
    padding: 16px;
    max-height: 400px;
    overflow-y: auto;
}

.icon-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
}

.icon-item {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
    background: white;
    border: 2px solid #e9ecef;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    aspect-ratio: 1;
    min-height: 50px;
    overflow: hidden;
    box-sizing: border-box;
}

.icon-item:hover {
    border-color: #007bff;
    background: #f8f9ff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
}

.icon-item.selected {
    border-color: #007bff;
    background: #e7f3ff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.icon-display {
    font-size: 22px;
    color: #424242;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    line-height: 1;
    padding: 0;
    margin: 0;
    box-sizing: border-box;
}

/* Material Icons 모든 스타일 일관성 유지 */
.icon-display.material-icons,
.icon-display.material-icons-outlined,
.icon-display.material-icons-round,
.icon-display.material-icons-sharp,
.icon-display.material-icons-two-tone {
    font-size: 22px;
    line-height: 1;
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin: 0;
}
/* 불필요한 before pseudo-element 제거 */
.icon-grid::before,
.icon-grid:before {
    content: none !important;
    display: none !important;
}

.icon-item.selected .icon-display {
    color: #007bff;
}

.check-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    font-size: 12px;
    color: #007bff;
    background: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 반응형 */
@media (max-width: 480px) {
    .icon-dropdown {
        width: 320px;
    }

    .icon-grid {
        grid-template-columns: repeat(6, 1fr);
        gap: 6px;
    }

    .icon-item {
        min-height: 50px;
        padding: 8px;
    }

    .icon-display {
        font-size: 24px;
    }
}
</style>
